import { useEffect, useState, useRef } from "react";
import { Search, Plus, Trash2, Edit, Folder, AlertCircle, School, Network, ChevronDown, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";
import Header from "../../components/Header";
import RoomModal from "../components/RoomModal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { useNavigate } from "react-router-dom";
import api from "../../connector/api";

const menuItems = [
  {
    name: "Rooms Management",
    icon: <School size={15} />,
    children: [
      { name: "Room Equipment", path: "/inventory/room-equipment" },
    ],
  },
  {
    name: "IT Infrastructure",
    icon: <Network size={15} />,
    children: [
      { name: "CCTV Camera", path: "/inventory/cctv-camera" },
      { name: "Desktop Computer", path: "/inventory/desktop-computer" },
      { name: "Media Equipment", path: "/inventory/media-equipment" },
      { name: "Network Equipment", path: "/inventory/network-equipment" },
    ],
  },
];

const SkeletonCard = () => (
  <div className="bg-white border border-slate-100 rounded-2xl p-5 space-y-4 animate-pulse shadow-sm">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-slate-100 rounded-xl" />
      <div className="space-y-2 flex-1">
        <div className="h-3.5 bg-slate-200 rounded w-1/3" />
        <div className="h-2.5 bg-slate-100 rounded w-2/3" />
      </div>
    </div>
    <div className="h-px bg-slate-100" />
    <div className="flex justify-end gap-2">
      <div className="w-16 h-7 bg-slate-100 rounded-lg" />
      <div className="w-8 h-7 bg-slate-100 rounded-lg" />
    </div>
  </div>
);

export default function RoomEquipment() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeSection, setActiveSection] = useState("ALL"); 
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); 
  const navRef = useRef(null);
  const navigate = useNavigate();

  // Fetch rooms directory registers
  useEffect(() => {
    let isMounted = true;
    
    const fetchRooms = async () => {
      try {
        const res = await api.get("rooms/");
        const roomsArray = Array.isArray(res.data) ? res.data : (res.data.results || []);
        if (isMounted) setRooms(roomsArray);
      } catch (err) {
        if (isMounted) toast.error("Failed to fetch rooms directory registers");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchRooms();
    return () => { isMounted = false; }; 
  }, []);

  // Click outside navigation handler to safely dismiss floating module dropdown windows
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Safe execution pipeline linking forms back up to our Django REST database tables
  const handleSaveRoom = async (cleanPayload) => {
    if (!cleanPayload.code) {
      toast.error("Operational Zone Identity Code is required.");
      return;
    }

    if (selectedRoom) {
      try {
        const res = await api.put(`rooms/${selectedRoom.id}/`, cleanPayload);
        
        setRooms((prev) => 
          prev.map((r) => (r.id === selectedRoom.id ? res.data : r))
        );
        
        toast.success("Room directory updated successfully");
        setIsModalOpen(false);
        setSelectedRoom(null);
      } catch (err) {
        toast.error("Failed to update room layout");
        console.error("Transmission Failure Details:", err.response?.data || err);
      }
    } else {
      try {
        const res = await api.post("rooms/", cleanPayload);
        
        setRooms((prev) => [res.data, ...prev]);
        toast.success("New structural room provisioned");
        
        setIsModalOpen(false);
        setSelectedRoom(null);
      } catch (err) {
        toast.error("Failed to append room structural node");
        console.error("Transmission Failure Details:", err.response?.data || err);
      }
    }
  };

  const handleDeleteRoom = async () => {
    if (!selectedRoom) return;
    try {
      await api.delete(`rooms/${selectedRoom.id}/`);
      setRooms((prev) => prev.filter((r) => r.id !== selectedRoom.id));
      toast.success("Room dropped successfully");
    } catch (err) {
      toast.error("Failed to delete room tracking point");
      console.error(err);
    } finally {
      setDeleteModalOpen(false);
      setSelectedRoom(null);
    }
  };

  const filteredRooms = rooms.filter((room) => {
    if (!room || !room.code) return false;
    
    const matchesSearch = 
      room.code.toLowerCase().includes(search.toLowerCase()) || 
      (room.description && room.description.toLowerCase().includes(search.toLowerCase()));
    
    const currentSectionClean = room.section ? String(room.section).toUpperCase().trim() : "GENERAL";

    if (activeSection === "ELEMENTARY") {
      return matchesSearch && currentSectionClean === "ELEMENTARY";
    }
    
    if (activeSection === "SECONDARY") {
      return matchesSearch && currentSectionClean === "SECONDARY";
    }
    if (activeSection === "HIGH_SCHOOL") {
      return matchesSearch && currentSectionClean === "HIGH_SCHOOL";
    }
    return matchesSearch;
  });

  const totalCount = rooms.length;
  
  const elementaryCount = rooms.filter(r => 
    r && r.section && String(r.section).trim().toUpperCase() === "ELEMENTARY"
  ).length;
  const secondaryCount = rooms.filter(r => 
    r && r.section && String(r.section).trim().toUpperCase() === "SECONDARY"
  ).length;
  
  const highSchoolCount = rooms.filter(r => 
    r && r.section && String(r.section).trim().toUpperCase() === "HIGH_SCHOOL"
  ).length;

  const sectionLabels = {
    ELEMENTARY: "Elementary School",
    SECONDARY: "Secondary School",
    HIGH_SCHOOL: "High School",
    GENERAL: "General / Other",
  };

  return (
    <div className="flex flex-col min-h-screen bg-slate-50/50 font-dynamic-title text-xs text-slate-600 font-medium tracking-normal">
      
      <header className="w-full bg-white border-b border-slate-100 shadow-xs z-30">
        <Header />
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        
        {/* Module Sub-Header Navigation Bar Container */}
        <div className="bg-white border border-slate-200/60 shadow-xs rounded-xl px-4 py-2 flex items-center justify-between relative" ref={navRef}>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 border-r border-slate-200 pr-3 mr-1 hidden sm:inline">
              Modules
            </span>
            
            {menuItems.map((item) => {
              const isDropdownOpen = activeDropdown === item.name;
              return (
                <div key={item.name} className="relative">
                  <button
                    onClick={() => setActiveDropdown(isDropdownOpen ? null : item.name)}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer ${
                      isDropdownOpen 
                        ? 'bg-slate-900 text-white shadow-xs' 
                        : 'text-slate-600 hover:bg-slate-100/80'
                    }`}
                  >
                    <span className={isDropdownOpen ? 'text-slate-300' : 'text-slate-400'}>
                      {item.icon}
                    </span>
                    {item.name}
                    <ChevronDown size={13} className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-56 bg-white border border-slate-200/90 rounded-xl shadow-xl z-50 p-1.5 animate-in fade-in zoom-in-95 duration-100">
                      {item.children.map((child) => (
                        <button
                          key={child.name}
                          onClick={() => {
                            setActiveDropdown(null);
                            navigate(child.path);
                          }}
                          className="w-full text-left flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-[#002855] rounded-lg transition-all group cursor-pointer"
                        >
                          <span>{child.name}</span>
                          <ChevronRight size={12} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-all transform translate-x-[-4px] group-hover:translate-x-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="hidden md:flex items-center gap-2 text-[10px] font-mono font-bold text-slate-400 bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            CAMPUS NODES: {totalCount} ACTIVE
          </div>
        </div>

        {/* Workspace Title & Controls Box */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 bg-white p-4 border border-slate-100 rounded-xl shadow-xs">
          <div>
            <h1 className="text-base font-bold text-slate-900 tracking-tight">Room Architecture</h1>
            <p className="text-[11px] text-slate-400 font-medium mt-0.5">Segmented inventory mapping matrix across education divisions.</p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="bg-slate-100/80 p-1 rounded-xl flex items-center gap-0.5 border border-slate-200/40">
              <button 
                onClick={() => setActiveSection("ALL")}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold tracking-tight transition-all cursor-pointer ${activeSection === "ALL" ? "bg-white text-slate-900 shadow-2xs" : "text-slate-500 hover:text-slate-800"}`}
              >
                All <span className={`ml-1 px-1 py-0.5 text-[9px] font-mono rounded bg-slate-200/50 ${activeSection === "ALL" ? "text-[#002855]" : "text-slate-400"}`}>{totalCount}</span>
              </button>
              <button 
                onClick={() => setActiveSection("ELEMENTARY")}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold tracking-tight transition-all cursor-pointer ${activeSection === "ELEMENTARY" ? "bg-white text-slate-900 shadow-2xs" : "text-slate-500 hover:text-slate-800"}`}
              >
                Elementary <span className="ml-1 px-1 py-0.5 text-[9px] font-mono rounded bg-slate-200/50 text-slate-400">{elementaryCount}</span>
              </button>
              <button 
                onClick={() => setActiveSection("SECONDARY")}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold tracking-tight transition-all cursor-pointer ${activeSection === "SECONDARY" ? "bg-white text-slate-900 shadow-2xs" : "text-slate-500 hover:text-slate-800"}`}
              >
                Secondary <span className="ml-1 px-1 py-0.5 text-[9px] font-mono rounded bg-slate-200/50 text-slate-400">{secondaryCount}</span>
              </button>
              <button 
                onClick={() => setActiveSection("HIGH_SCHOOL")}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold tracking-tight transition-all cursor-pointer ${activeSection === "HIGH_SCHOOL" ? "bg-white text-slate-900 shadow-2xs" : "text-slate-500 hover:text-slate-800"}`}
              >
                High School <span className="ml-1 px-1 py-0.5 text-[9px] font-mono rounded bg-slate-200/50 text-slate-400">{highSchoolCount}</span>
              </button>
            </div>

            <div className="relative flex items-center bg-slate-50 border border-slate-200/80 rounded-xl focus-within:ring-2 focus-within:ring-[#002855]/5 focus-within:border-[#002855] transition-all sm:w-56">
              <Search size={14} className="absolute left-3.5 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Find room node..."
                className="w-full pl-9 pr-4 py-2 bg-transparent text-slate-800 placeholder-slate-400 outline-none"
              />
            </div>

            <button
              onClick={() => {
                setSelectedRoom(null);
                setIsModalOpen(true);
              }}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 font-medium uppercase tracking-wider text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-90 rounded-xl shadow-xs transition-all cursor-pointer active:scale-98"
            >
              <Plus size={14} className="stroke-[3px]" />
              Provision Room
            </button>
          </div>
        </div>

        {/* Dynamic Card Dashboard View Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, idx) => (
              <SkeletonCard key={idx} />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredRooms.map((room) => {
                const normalizedSection = room.section ? String(room.section).toUpperCase().trim() : "GENERAL";
                const labelText = sectionLabels[normalizedSection] || sectionLabels.GENERAL;

                return (
                  <div
                    key={room.id}
                    onClick={() => navigate(`/inventory/room-equipment/${room.id}`)}
                    className="bg-white border border-slate-100 rounded-xl p-4 shadow-2xs hover:shadow-md hover:border-slate-300 hover:-translate-y-1 hover:ring-1 hover:ring-slate-950/5 transition-all duration-300 ease-out flex flex-col justify-between group cursor-pointer"
                  >
                    <div className="space-y-3.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="p-2 bg-slate-50 border border-slate-100 text-slate-400 rounded-xl shrink-0 group-hover:bg-slate-100/70 transition-colors">
                            <Folder size={16} className="fill-slate-100 text-slate-400 group-hover:text-slate-800 group-hover:fill-slate-200 transition-colors" />
                          </div>
                          <div className="min-w-0">
                            <span className="block font-bold text-slate-900 text-[13px] tracking-tight truncate group-hover:text-slate-950 transition-colors">
                              {room.code}
                            </span>
                            <span className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded-md mt-1 border ${
                              normalizedSection === "ELEMENTARY"
                                ? "bg-indigo-50/60 text-indigo-700 border-indigo-100/50"
                                : normalizedSection === "HIGH_SCHOOL"
                                ? "bg-amber-50/60 text-amber-700 border-amber-100/50"
                                : "bg-slate-50 text-slate-600 border-slate-200/60"
                            }`}>
                              {labelText}
                            </span>
                          </div>
                        </div>
                      </div>

                      <p className="text-slate-500 font-medium leading-relaxed line-clamp-2 h-8 group-hover:text-slate-600 transition-colors">
                        {room.description || "No supplemental details documented for this zone."}
                      </p>
                    </div>

                    {/* Action Panel Container */}
                    <div className="flex items-center justify-end pt-3 border-t border-slate-100 mt-4">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation(); // Stops the master card element navigation click stream trigger
                            setSelectedRoom(room);
                            setIsModalOpen(true);
                          }}
                          className="p-1.5 bg-slate-50 hover:bg-amber-50 text-slate-400 hover:text-amber-600 rounded-lg border border-slate-100 transition-colors cursor-pointer"
                          title="Edit Configuration"
                        >
                          <Edit size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation(); // Stops the master card element navigation click stream trigger
                            setSelectedRoom(room);
                            setDeleteModalOpen(true);
                          }}
                          className="p-1.5 bg-slate-50 hover:bg-rose-50 text-slate-400 hover:text-rose-600 rounded-lg border border-slate-100 transition-colors cursor-pointer"
                          title="Decommission Zone"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredRooms.length === 0 && (
              <div className="flex flex-col items-center justify-center text-center p-12 bg-white rounded-2xl border border-dashed border-slate-200">
                <div className="p-3 bg-slate-50 text-slate-400 rounded-full border border-slate-100 mb-3">
                  <AlertCircle size={20} />
                </div>
                <h3 className="font-bold text-slate-800 text-sm">No functional zones matched</h3>
                <p className="text-slate-400 mt-0.5 max-w-xs">Refine your active string query or toggle your selected division standard.</p>
              </div>
            )}
          </>
        )}
      </main>

      <RoomModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRoom(null);
        }}
        onSave={handleSaveRoom}
        room={selectedRoom}
      />

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedRoom(null);
        }}
        onConfirm={handleDeleteRoom}
        title="Delete Room"
        itemName={selectedRoom?.code}
      />
    </div>
  );
}